/***
 *
 *   PROJECT SELECTOR
 *   Active-project switcher in the top bar. Ported from
 *   ui-reference/src/components/layouts/project-switcher.tsx: a `ProjectMark`
 *   avatar, the truncated name and a chevron, over a `w-56` menu with a mark per
 *   project, a `Public` badge and a check on the active one.
 *
 *   Kept from the live version, and absent from the reference:
 *     - "All projects", which is a real state here (`isAllProjectSelected`)
 *     - creating a project from the menu, for anyone above `user`
 *   The old hover-to-open HoverNav is gone; this is click-to-open, which is what
 *   the reference does and what makes it reachable from a keyboard.
 *
 **********/

import { useContext, memo } from 'react';
import { useQueryClient } from 'react-query';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { Badge, Button, Icon, ViewContext, useProjectContext } from 'components/lib';
import { ProjectMark } from 'components/layout/projectMark';
import { Menu } from 'components/dropdown/menu';
import Style from './selector.tailwind.js';

const form = (_t) => ({
    logo: {
        label: 'Logo',
        type: 'logo',
        required: false,
        placeholder: '',
    },
    name: {
        label: _t('project.name'),
        type: 'text',
        required: true,
    },
    privacy: {
        label: _t('project.privacy.label'),
        type: 'select',
        required: true,
        options: [
            { value: 'private', label: _t('project.privacy.private') },
            { value: 'public', label: _t('project.privacy.public') },
        ],
        default: 'private',
    },
});

export const ProjectSelector = memo(() => {
    const { _t } = useTranslate();
    const queryClient = useQueryClient();
    const viewContext = useContext(ViewContext);
    const context = useContext(AuthContext);
    const { permission } = context?.user || {};
    const { projects, switchProject, selectAllProjects, isAllProjectSelected, selectedProject } =
        useProjectContext();

    const hasEditPermission = permission !== 'contact' && permission !== 'user';

    if (!projects) return null;

    function createProject() {
        viewContext.modal.show(
            {
                title: _t('project.create.label'),
                form: form(_t),
                buttonText: _t('save'),
                url: `/api/project`,
                method: 'POST',
            },
            () => {
                queryClient.refetchQueries('project_users');
                window.location.reload();
            }
        );
    }

    // No projects yet: the switcher has nothing to switch, so the slot carries
    // the create affordance instead — as it did before.
    if (!projects.length)
        return hasEditPermission ? (
            <Button small text={_t('project.create.label')} action={createProject} />
        ) : null;

    const activeName = selectedProject?.name || _t('project.all');

    return (
        <Menu
            align="end"
            width="w-56"
            label={_t('project.label')}
            triggerClassName={Style.trigger}
            trigger={
                <>
                    {selectedProject ? (
                        <ProjectMark name={selectedProject.name} filePath={selectedProject.file_path} />
                    ) : (
                        <Icon image="folder" size={16} color="currentColor" className={Style.allIcon} />
                    )}
                    <span className={Style.name}>{activeName}</span>
                    <Icon image="chevron-down" size={14} color="currentColor" className={Style.chevron} />
                </>
            }
            list={[
                { label: _t('project.label.multiple'), heading: true },
                { separator: true },

                ...projects.map((project) => ({
                    label: (
                        <>
                            <ProjectMark name={project.name} filePath={project.file_path} />
                            <span className={Style.itemName}>{project.name}</span>
                            {project.privacy === 'public' && (
                                <Badge variant="neutral" text={_t('public')} className={Style.itemBadge} />
                            )}
                        </>
                    ),
                    checked: project.id === selectedProject?.id,
                    action: () => switchProject(project.id),
                })),

                ...(projects.length > 1
                    ? [
                          {
                              label: _t('project.all'),
                              checked: isAllProjectSelected,
                              action: selectAllProjects,
                          },
                      ]
                    : []),

                ...(hasEditPermission
                    ? [
                          { separator: true },
                          { label: _t('project.create.new'), icon: 'plus', action: createProject },
                      ]
                    : []),
            ]}
        />
    );
});
